import {
  certifications,
  education,
  personalInfo,
  workExperiences,
} from '@/data/portfolio'

const stripHtml = (value = '') => value.replace(/<[^>]*>/g, '').trim()

const formatRole = (experience: (typeof workExperiences)[number]): string => {
  const client = [experience.finalClient, stripHtml(experience.project)].filter(Boolean).join(' · ')
  return `**${experience.title} — ${experience.company}**${client ? ` (${client})` : ''}`
}

const searchableText = (experience: (typeof workExperiences)[number]): string =>
  [
    experience.title,
    experience.company,
    experience.finalClient,
    experience.project,
    experience.industry,
    ...(experience.role || []),
    ...(experience.tasks || []),
    ...(experience.achievements || []),
    ...(experience.skills || []).map(skill => skill.label),
  ]
    .join(' ')
    .toLowerCase()

const STOP_WORDS = new Set([
  'about', 'and', 'are', 'can', 'does', 'experience', 'for', 'have', 'his',
  'how', 'in', 'is', 'jonathan', 'me', 'of', 'on', 'tell', 'the', 'what',
  'with', 'work', 'you', 'your',
])

export const queryLocalCv = (question: string): string => {
  const query = question.toLowerCase()
  const current = workExperiences.find(experience => !experience.endDate) || workExperiences[0]

  if (/certificat|qualification/.test(query)) {
    return certifications.length
      ? `Jonathan's listed certification${certifications.length === 1 ? ' is' : 's are'}:\n\n${certifications
          .map(certification => `- **${certification.name}**, ${certification.issuer} (${certification.date})`)
          .join('\n')}`
      : 'No certifications are currently listed on the CV.'
  }

  if (/education|degree|university|studied|study/.test(query)) {
    return education
      .map(item => `- **${item.degree}**, ${item.institution} (${item.year})`)
      .join('\n')
  }

  if (/contact|email|reach|hire/.test(query)) {
    return `You can contact Jonathan at **${personalInfo.email}** or use the LinkedIn link in the CV header.`
  }

  if (/current|latest|present|now|recent/.test(query)) {
    return `${formatRole(current)}\n\n${current.tasks.slice(0, 4).map(task => `- ${task}`).join('\n')}`
  }

  if (/skill|technolog|stack|tool|language/.test(query)) {
    const currentSkills = current.skills.map(skill => skill.label)
    return [
      `Jonathan specialises in **${personalInfo.skills.primary.join(', ')}**.`,
      `\nCore technologies: ${personalInfo.skills.technologies.join(', ')}.`,
      `\nCurrent platform stack: ${currentSkills.join(', ')}.`,
      `\nProgramming languages: ${personalInfo.skills.languages.join(', ')}.`,
    ].join('')
  }

  if (/career|background|experience|history|roles?/.test(query)) {
    const recentRoles = workExperiences.slice(0, 5).map(formatRole).join('\n- ')
    return `${personalInfo.summary}\n\nRecent roles:\n\n- ${recentRoles}`
  }

  const terms = query
    .replace(/[^a-z0-9+#.]+/g, ' ')
    .split(/\s+/)
    .filter(term => term.length > 1 && !STOP_WORDS.has(term))

  const matches = workExperiences
    .map(experience => ({
      experience,
      score: terms.reduce(
        (total, term) => total + (searchableText(experience).includes(term) ? 1 : 0),
        0,
      ),
    }))
    .filter(match => match.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)

  if (matches.length > 0) {
    return matches
      .map(({ experience }) => {
        const details = [...(experience.achievements || []), ...(experience.tasks || [])].slice(0, 3)
        return `${formatRole(experience)}\n${details.map(detail => `- ${detail}`).join('\n')}`
      })
      .join('\n\n')
  }

  return `I couldn't find a precise match in the CV. Try asking about Jonathan's **current role, platform engineering, AWS, DevOps, leadership, skills, certifications, or career history**.`
}
